import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import AutocompleteBase from "../autocomplete-base";
import {AutocompleteFreeSoloValueMapping, AutocompleteProps, AutocompleteRenderOptionState, TextFieldProps} from "@mui/material";
import {CancelToken} from "axios";
import {BizFormApiClient} from "../../../../../packages/biz-form";


interface FormsOption {
    id: string;
    title: string;
}

interface FormsAutocompleteProps<T = FormsOption, M extends boolean = false, DC extends boolean = false, FS extends boolean = false> extends Omit<AutocompleteProps<T, M, DC, FS>, 'renderInput' | 'renderOption' | 'options'> {
    textFieldProps?: Partial<TextFieldProps>;
    api: BizFormApiClient,
    form?: boolean;
    name?: string;
    required?: boolean;
    setLoadingOptions?: (loading: boolean) => void;
    transformRecord?: (record: FormsOption) => T;
    /** When set, filters this list locally instead of calling listForms (e.g. import preload). */
    cachedForms?: FormsOption[];
    renderOption?: (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: T,
        state: AutocompleteRenderOptionState,
        defaultRender: (props: React.HTMLAttributes<HTMLLIElement>, option: T) => React.ReactNode
    ) => React.ReactNode;

    [key: string]: any;
}


const FormsAutocomplete = <T = FormsOption, M extends boolean = false, DC extends boolean = false, FS extends boolean = false>({
                                                                                                                                   textFieldProps = {},
                                                                                                                                   form = false,
                                                                                                                                   name = undefined,
                                                                                                                                   required = false,
                                                                                                                                   setLoadingOptions: setLoadingOptionsProps,
                                                                                                                                   renderOption: customRenderOption,
                                                                                                                                   transformRecord,
                                                                                                                                   api,
                                                                                                                                   cachedForms,
                                                                                                                                   ...props
                                                                                                                               }: FormsAutocompleteProps<T, M, DC, FS>) => {
    const isMounted = useIsMounted();
    const [loadingOptions, setLoadingOptions] = useState(false);

    /**
     * Syncs the internal loading state with the parent component
     */
    useEffect(() => {
        setLoadingOptionsProps?.(loadingOptions);
    }, [loadingOptions, setLoadingOptionsProps]);

    /**
     * Fetches Forms options based on search keywords
     * Handles loading state and component unmounting
     *
     * @param cancelToken - Axios cancel token for request cancellation
     * @param keywords - Search query string
     * @returns Array of matching Forms options
     */
    const getOptions = useCallback(async (cancelToken: CancelToken, keywords?: string) => {
        setLoadingOptions(true);
        try {
            if (cachedForms?.length) {
                const kw = (keywords ?? '').trim().toLowerCase();
                const list = cachedForms.map((e) => transformRecord?.(e) ?? (e as T));
                if (!kw) {
                    return list;
                }
                return list.filter((item) => {
                    const title = (item as FormsOption)?.title ?? '';
                    return title.toLowerCase().includes(kw);
                });
            }
            // @ts-ignore - API type definition is not complete
            const response = await api.listForms({
                pagination: {pageSize: 20, currentPage: 1},
                keyword: keywords,
                sorts: [],
            }, cancelToken)
            if (!isMounted())
                return [] as T[];
            return response?.data?.items?.map(e => transformRecord?.(e) ?? e as T) ?? [];
        } finally {
            if (isMounted()) {
                setLoadingOptions(false);
            }
        }
    }, [api, cachedForms, isMounted, transformRecord]);

    /**
     * Determines if two Forms options are equal based on their IDs
     */
    const isOptionEqualToValue = useCallback((option: FormsOption, value: FormsOption | null) =>
        option.id === value?.id, []);

    /**
     * Extracts the display label from a Forms option
     */
    const getOptionLabel = useCallback((option: FormsOption | AutocompleteFreeSoloValueMapping<FS>) => props.freeSolo === true ? option : `${(option as FormsOption)?.title ?? ""}`, [props.freeSolo]);

    /**
     * Gets the unique key for a Forms option
     */
    const getOptionKey = useCallback((option: FormsOption | AutocompleteFreeSoloValueMapping<FS>) => props.freeSolo === true ? option : (option as FormsOption)?.id ?? "", [props.freeSolo]);

    // GetOptions will populate an initial empty array
    const defaultOptions = useMemo(() => [], []);

    const useCachedForms = !!cachedForms?.length;

    return (
        <AutocompleteBase<T, M, DC, FS>
            keywordsBased={!useCachedForms}
            canSearchEmptyKeywords
            getOptions={getOptions}
            timeout={useCachedForms ? 0 : 1000}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue as (option: T, value: T | null) => boolean}
            getOptionLabel={getOptionLabel as (option: T | AutocompleteFreeSoloValueMapping<FS>) => string}
            getOptionKey={getOptionKey as (option: T | AutocompleteFreeSoloValueMapping<FS>) => string}
            options={defaultOptions}
            clearOnBlur={false}
            {...props}
        />
    );
};

export default FormsAutocomplete;